import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import PropTypes from 'prop-types';
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import RotateIcon from '@mui/icons-material/Rotate90DegreesCcw';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { FileTypes, getFileType } from 'src/utils/fileTypes';
import { Document, Page, pdfjs } from 'react-pdf';
import getFileFromExternalLink from 'src/utils/document';
import * as ExcelJS from 'exceljs';
import {
  useReactTable,
  getCoreRowModel,
  flexRender
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import useStyles from './styles';
import DialogTransition from '../DialogTransition';

const MemoizedCell = React.memo(({ cell, className }) => {
  const renderedCell = flexRender(
    cell.column.columnDef.cell,
    cell.getContext()
  );
  return (
    <TableCell key={cell.id} className={className}>
      {renderedCell}
    </TableCell>
  );
});

MemoizedCell.propTypes = {
  cell: PropTypes.shape({
    id: PropTypes.string.isRequired,
    column: PropTypes.shape({
      columnDef: PropTypes.shape({
        cell: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.node,
          PropTypes.func
        ]).isRequired
      }).isRequired
    }).isRequired,
    getContext: PropTypes.func.isRequired
  }).isRequired,
  className: PropTypes.string
};

const MemoizedHeader = React.memo(({ header, classes }) => {
  const renderedHeader = flexRender(
    header.column.columnDef.header,
    header.getContext()
  );
  return (
    <TableCell key={header.id} className={classes.headerCell}>
      {renderedHeader}
    </TableCell>
  );
});

MemoizedHeader.propTypes = {
  header: PropTypes.shape({
    id: PropTypes.string.isRequired,
    column: PropTypes.shape({
      columnDef: PropTypes.shape({
        header: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.node,
          PropTypes.func
        ]).isRequired
      }).isRequired
    }).isRequired,
    getContext: PropTypes.func.isRequired
  }).isRequired,
  classes: PropTypes.object.isRequired
};

const FETCH_TIMEOUT = 30000; // 30 seconds timeout

const PDF_ZOOM_MIN = 0.5;
const PDF_ZOOM_MAX = 1.5;
const PDF_ZOOM_STEP = 0.25;

// Estimated page height before the real <Page> measures itself.
// A4/Letter portrait ratio (~1.414) is only a fallback for the brief window
// before onDocumentLoadSuccess resolves the real first-page ratio;
// react-virtual's measureElement corrects it once each canvas renders.
const DEFAULT_PDF_PAGE_ASPECT_RATIO = 1.414;
const PDF_PAGE_GAP = 20; // marginBottom between pages
const PDF_OVERSCAN = 2; // +2 pages of margin (Definition of Done)

const PDF_BREAKPOINTS = { xs: 600, sm: 960, md: 1280 };
const PDF_WIDTH_CONFIG = {
  xs: { factor: 0.95, padding: 32 },
  sm: { factor: 0.92, padding: 48 },
  md: { max: 1100, factor: 0.85 },
  lg: { max: 1400, factor: 0.75 }
};

const getResponsivePageWidth = () => {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
  if (vw < PDF_BREAKPOINTS.xs) {
    return Math.floor(vw * PDF_WIDTH_CONFIG.xs.factor) - PDF_WIDTH_CONFIG.xs.padding;
  }
  if (vw < PDF_BREAKPOINTS.sm) {
    return Math.floor(vw * PDF_WIDTH_CONFIG.sm.factor) - PDF_WIDTH_CONFIG.sm.padding;
  }
  if (vw < PDF_BREAKPOINTS.md) {
    return Math.min(
      PDF_WIDTH_CONFIG.md.max,
      Math.floor(vw * PDF_WIDTH_CONFIG.md.factor)
    );
  }
  return Math.min(
    PDF_WIDTH_CONFIG.lg.max,
    Math.floor(vw * PDF_WIDTH_CONFIG.lg.factor)
  );
};

const mapFetchError = err => {
  if (err.message === 'REQUEST_TIMEOUT') return 'COMMON.REQUEST_TIMEOUT';
  if (err.message.startsWith('HTTP_ERROR_')) {
    const statusCode = parseInt(err.message.split('_')[2], 10);
    if (statusCode === 404) return 'COMMON.FILE_NOT_FOUND';
    if (statusCode === 403) return 'COMMON.ACCESS_DENIED';
    if (statusCode >= 500) return 'COMMON.SERVER_ERROR';
  }
  if (!navigator.onLine) return 'COMMON.NO_INTERNET_CONNECTION';
  return null;
};

const fetchWithTimeout = async (url, options = {}) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    return response;
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('REQUEST_TIMEOUT');
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
};

const DocumentViewerDialog = ({ open, onClose, documentUrl, fileName }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [rotation, setRotation] = useState(0);
  const [zoomed, setZoomed] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [loadingExcel, setLoadingExcel] = useState(false);
  const [loadingText, setLoadingText] = useState(false);
  const [error, setError] = useState(null);
  const [excelData, setExcelData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [excelTitle, setExcelTitle] = useState('');
  const [textContent, setTextContent] = useState('');
  const [pageWidth, setPageWidth] = useState(getResponsivePageWidth());
  const [pdfZoomLevel, setPdfZoomLevel] = useState(1);
  const [pageAspectRatio, setPageAspectRatio] = useState(
    DEFAULT_PDF_PAGE_ASPECT_RATIO
  );
  const pdfScrollRef = useRef(null);

  const renderedPageWidth = Math.floor(pageWidth * pdfZoomLevel);
  const estimatedPageHeight =
    Math.floor(renderedPageWidth * pageAspectRatio) + PDF_PAGE_GAP;

  const pageVirtualizer = useVirtualizer({
    count: numPages || 0,
    getScrollElement: () => pdfScrollRef.current,
    estimateSize: () => estimatedPageHeight,
    overscan: PDF_OVERSCAN
  });

  useEffect(() => {
    if (documentUrl) {
      setNumPages(null);
      setError(null);
      setExcelData([]);
      setColumns([]);
      setExcelTitle('');
      setTextContent('');
      setPdfZoomLevel(1);
      setPageAspectRatio(DEFAULT_PDF_PAGE_ASPECT_RATIO);
      if (pdfScrollRef.current) {
        pdfScrollRef.current.scrollTop = 0;
      }
      // react-virtual's itemSizeCache is keyed by index, not by document,
      // so switching documents must clear it — otherwise the new document's
      // early pages inherit the previous document's measured heights.
      pageVirtualizer.measure();
    }
  }, [documentUrl, pageVirtualizer]);

  // Re-measure page heights when zoom or viewport width changes, so the
  // virtual scroll length and item offsets stay accurate.
  useEffect(() => {
    pageVirtualizer.measure();
  }, [renderedPageWidth, pageVirtualizer]);

  const handlePdfZoomIn = () => {
    setPdfZoomLevel(prev => Math.min(prev + PDF_ZOOM_STEP, PDF_ZOOM_MAX));
  };

  const handlePdfZoomOut = () => {
    setPdfZoomLevel(prev => Math.max(prev - PDF_ZOOM_STEP, PDF_ZOOM_MIN));
  };

  const zoomImage = () => {
    setZoomed(!zoomed);
  };

  const handleRotate = () => {
    setRotation(rotation + 90);
  };

  useEffect(() => {
    pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;
  }, []);

  useEffect(() => {
    if (!open) return () => {};

    const updatePageWidth = () => setPageWidth(getResponsivePageWidth());
    updatePageWidth();
    window.addEventListener('resize', updatePageWidth);
    return () => window.removeEventListener('resize', updatePageWidth);
  }, [open]);

  const onDocumentLoadSuccess = pdf => {
    setNumPages(pdf.numPages);
    pdf
      .getPage(1)
      .then(page => {
        const { width, height } = page.getViewport({ scale: 1 });
        if (width > 0) {
          setPageAspectRatio(height / width);
        }
      })
      .catch(() => {});
  };

  const table = useReactTable({
    data: excelData,
    columns,
    getCoreRowModel: getCoreRowModel()
  });

  const memoizedTableContent = useMemo(() => {
    if (!excelData.length) return null;

    return (
      <Box className={classes.excelWrapper}>
        {excelTitle && (
          <Box className={classes.excelTitleBanner}>{excelTitle}</Box>
        )}
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ maxHeight: '68vh', borderRadius: 0 }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              {table.getHeaderGroups().map(headerGroup => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <MemoizedHeader
                      key={header.id}
                      header={header}
                      classes={classes}
                    />
                  ))}
                </TableRow>
              ))}
            </TableHead>
            <TableBody>
              {table.getRowModel().rows.map((row, rowIdx) => {
                if (row.original._isMergedRow) return null;
                return (
                  <TableRow
                    key={row.id}
                    sx={{
                      backgroundColor: rowIdx % 2 === 0 ? '#fff' : '#f4f7fb',
                      '&:hover': { backgroundColor: '#e8f0fe' },
                      '&:last-child td': { borderBottom: 'none' }
                    }}
                  >
                    {row.getVisibleCells().map(cell => (
                      <MemoizedCell
                        key={cell.id}
                        cell={cell}
                        className={classes.excelDataCell}
                      />
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }, [table, excelData.length, classes, excelTitle]);

  useEffect(() => {
    const shouldFetchExcelFile = () => {
      return (
        open &&
        documentUrl &&
        getFileType(documentUrl) === FileTypes.SPREADSHEET
      );
    };

    const loadExcelFile = async () => {
      if (shouldFetchExcelFile()) {
        setLoadingExcel(true);
        setError(null);
        try {
          const response = await fetchWithTimeout(documentUrl);
          if (!response.ok) {
            throw new Error(`HTTP_ERROR_${response.status}`);
          }
          const arrayBuffer = await response.arrayBuffer();
          const workbook = new ExcelJS.Workbook();
          await workbook.xlsx.load(arrayBuffer);

          // Get the first worksheet
          const worksheet = workbook.worksheets[0];

          if (worksheet && worksheet.rowCount > 0) {
            const totalCols = worksheet.columnCount;

            const getCellDisplayValue = cell => {
              if (cell.type === ExcelJS.ValueType.Merge) return '';
              const val = cell.value;
              if (val === null || val === undefined) return '';
              if (typeof val === 'object') {
                if (val.result !== undefined) return val.result ?? '';
                if (val.richText) return val.richText.map(r => r.text).join('');
                if (val.text) return val.text;
                return String(val);
              }
              return val;
            };

            // Find the first row with more than one non-merge, non-empty cell.
            // This skips merged title rows (e.g. "RESUMEN DE PAGOS" spanning all cols)
            // and lands on the real column-header row.
            let headerRowIndex = 1;
            for (let i = 1; i <= Math.min(worksheet.rowCount, 10); i++) {
              const row = worksheet.getRow(i);
              let nonMergeCount = 0;
              for (let j = 1; j <= totalCols; j++) {
                const cell = row.getCell(j);
                if (
                  cell.type !== ExcelJS.ValueType.Merge &&
                  cell.value != null &&
                  cell.value !== ''
                ) {
                  nonMergeCount++;
                }
              }
              if (nonMergeCount > 1) {
                headerRowIndex = i;
                break;
              }
            }

            // Collect any title text from rows above the header row
            const titleParts = [];
            for (let i = 1; i < headerRowIndex; i++) {
              const titleRow = worksheet.getRow(i);
              for (let j = 1; j <= totalCols; j++) {
                const cell = titleRow.getCell(j);
                if (cell.type !== ExcelJS.ValueType.Merge) {
                  const val = getCellDisplayValue(cell);
                  if (val) titleParts.push(String(val));
                }
              }
            }

            const headerRow = worksheet.getRow(headerRowIndex);

            // Only include columns whose header cell is not a merge slave
            const validColIndices = [];
            for (let j = 1; j <= totalCols; j++) {
              if (headerRow.getCell(j).type !== ExcelJS.ValueType.Merge) {
                validColIndices.push(j);
              }
            }

            if (validColIndices.length === 0) {
              setError(t('COMMON.EXCEL_FILE_PREVIEW_NOT_AVAILABLE'));
              return;
            }

            const headers = validColIndices.map(j =>
              getCellDisplayValue(headerRow.getCell(j))
            );

            // Get data rows (everything after the header row)
            const rows = [];
            for (let i = headerRowIndex + 1; i <= worksheet.rowCount; i++) {
              const row = worksheet.getRow(i);
              rows.push(
                validColIndices.map(j => getCellDisplayValue(row.getCell(j)))
              );
            }

            const getNormalizedKeys = headerArr => {
              const keyMap = {};
              return headerArr.map(header => {
                const baseKey = String(header ?? '')
                  .toLowerCase()
                  .replace(/[^a-z0-9]+/g, '_')
                  .replace(/^_+|_+$/g, '') || 'col';
                let uniqueKey = baseKey;
                let count = 1;
                while (Object.values(keyMap).includes(uniqueKey)) {
                  uniqueKey = `${baseKey}_${count++}`;
                }
                keyMap[String(header)] = uniqueKey;
                return uniqueKey;
              });
            };
            const normalizedKeys = getNormalizedKeys(headers);

            const cols = headers.map((header, idx) => ({
              accessorKey: normalizedKeys[idx],
              header: header != null ? String(header) : ''
            }));

            const rowData = rows.map(row => {
              const rowObj = {};
              normalizedKeys.forEach((key, index) => {
                rowObj[key] = row[index] ?? '';
              });
              // Rows where only one cell has a value are full-width merged rows
              // (e.g. TOTALES) — flag them so the table can render with colSpan
              const filledCount = row.filter(v => v !== '' && v != null).length;
              if (filledCount === 1) rowObj._isMergedRow = true;
              return rowObj;
            });

            setExcelTitle(titleParts.join(' '));
            setColumns(cols);
            setExcelData(rowData);
            setError(null);
          } else {
            setError(t('COMMON.EXCEL_FILE_PREVIEW_NOT_AVAILABLE'));
          }
        } catch (err) {
          const errorKey = mapFetchError(err) || 'COMMON.EXCEL_FILE_PREVIEW_ERROR';
          setError(t(errorKey));
        } finally {
          setLoadingExcel(false);
        }
      }
    };

    loadExcelFile();
  }, [open, documentUrl, t]);

  useEffect(() => {
    const shouldFetchTextFile = () => {
      return (
        open &&
        documentUrl &&
        getFileType(documentUrl) === FileTypes.TEXT
      );
    };

    const loadTextFile = async () => {
      if (shouldFetchTextFile()) {
        setLoadingText(true);
        setError(null);
        try {
          const response = await fetchWithTimeout(documentUrl);
          if (!response.ok) {
            throw new Error(`HTTP_ERROR_${response.status}`);
          }
          const text = await response.text();
          setTextContent(text);
          setError(null);
        } catch (err) {
          const errorKey = mapFetchError(err) || 'COMMON.TEXT_FILE_PREVIEW_ERROR';
          setError(t(errorKey));
        } finally {
          setLoadingText(false);
        }
      }
    };

    loadTextFile();
  }, [open, documentUrl, t]);

  if (!open) return null;

  const fileType = getFileType(documentUrl);
  const isExcel = fileType === FileTypes.SPREADSHEET;
  const isText = fileType === FileTypes.TEXT;

  return (
    <Dialog
      open={open}
      TransitionComponent={DialogTransition}
      classes={{ paper: classes.root }}
      keepMounted
      fullWidth
      maxWidth={false}
      aria-modal="true"
      aria-labelledby="document-viewer-dialog-title"
    >
      <DialogTitle
        id="document-viewer-dialog-title"
        sx={
          fileType === FileTypes.PDF
            ? {
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 1,
                position: 'relative'
              }
            : undefined
        }
      >
        {fileType === FileTypes.PDF ? (
          <Grid container justifyContent="center" spacing={1} sx={{ flex: 1 }}>
            <Grid item>
              <Button
                className={classes.buttons}
                size="small"
                onClick={handlePdfZoomOut}
                variant="contained"
                color="primary"
                startIcon={<ZoomOutIcon />}
                aria-label={t('COMMON.ZOOM_OUT')}
                disabled={pdfZoomLevel <= PDF_ZOOM_MIN}
                sx={{ minWidth: 'auto', py: 0.5 }}
              >
                {t('COMMON.ZOOM_OUT')}
              </Button>
            </Grid>
            <Grid item>
              <Button
                className={classes.buttons}
                size="small"
                onClick={handlePdfZoomIn}
                variant="contained"
                color="primary"
                startIcon={<ZoomInIcon />}
                aria-label={t('COMMON.ZOOM_IN')}
                disabled={pdfZoomLevel >= PDF_ZOOM_MAX}
                sx={{ minWidth: 'auto', py: 0.5 }}
              >
                {t('COMMON.ZOOM_IN')}
              </Button>
            </Grid>
          </Grid>
        ) : null}
        <IconButton
          key="close"
          aria-label={t('COMMON.CLOSE')}
          color="inherit"
          onClick={onClose}
          className={classes.closeIcon}
          sx={
            fileType === FileTypes.PDF
              ? { position: 'absolute', right: 8, top: 8 }
              : undefined
          }
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {isExcel && (
        <>
          <DialogContent
            className={classes.dialogContent}
            sx={{ alignItems: 'flex-start', justifyContent: 'flex-start', p: 3, overflow: 'auto' }}
          >
            {loadingExcel && <CircularProgress color="secondary" sx={{ alignSelf: 'center', mx: 'auto' }} />}
            {!loadingExcel && error && (
              <Typography variant="h6" align="center" color="error">
                {error}
              </Typography>
            )}
            {!loadingExcel && !error && memoizedTableContent}
          </DialogContent>
          <DialogActions className={classes.dialogActions}>
            <Grid container justifyContent="center">
              <Button
                className={classes.buttons}
                onClick={() => {
                  getFileFromExternalLink(documentUrl, fileName);
                }}
                variant="contained"
                color="primary"
                startIcon={<FileDownloadIcon />}
                aria-label={t('COMMON.DOWNLOAD')}
              >
                {t('COMMON.DOWNLOAD')}
              </Button>
            </Grid>
          </DialogActions>
        </>
      )}

      {isText && (
        <>
          <DialogContent className={classes.dialogContent}>
            {loadingText && <CircularProgress color="secondary" />}
            {!loadingText && error && (
              <Typography variant="h6" align="center" color="error">
                {error}
              </Typography>
            )}
            {!loadingText && !error && (
              <Typography
                variant="body1"
                component="pre"
                style={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontFamily: 'monospace',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  maxHeight: '70vh',
                  overflow: 'auto',
                  padding: '16px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px',
                  border: '1px solid #ddd'
                }}
              >
                {textContent}
              </Typography>
            )}
          </DialogContent>
          <DialogActions className={classes.dialogActions}>
            <Grid container justifyContent="center">
              <Button
                className={classes.buttons}
                onClick={() => {
                  getFileFromExternalLink(documentUrl, fileName);
                }}
                variant="contained"
                color="primary"
                startIcon={<FileDownloadIcon />}
                aria-label={t('COMMON.DOWNLOAD')}
              >
                {t('COMMON.DOWNLOAD')}
              </Button>
            </Grid>
          </DialogActions>
        </>
      )}

      {!isExcel && !isText && fileType !== FileTypes.PDF && (
        <>
          <DialogContent className={classes.dialogContent}>
            <img
              src={documentUrl}
              alt="Imagen"
              id="myImage"
              style={{
                transform: `rotate(${rotation}deg) scale(${zoomed ? 2 : 1})`,
                transition: 'transform 0.3s ease',
                borderRadius: '.5rem',
                display: 'block',
                maxHeight: '70%',
                maxWidth: '40%',
                objectFit: 'cover',
                margin: 'auto'
              }}
            />
          </DialogContent>
          <DialogActions className={classes.dialogActions}>
            <Button
              className={classes.buttons}
              onClick={handleRotate}
              variant="contained"
              color="primary"
              startIcon={<RotateIcon />}
              aria-label={t('COMMON.ROTATE')}
            >
              {t('COMMON.ROTATE')}
            </Button>
            <Button
              className={classes.buttons}
              onClick={zoomImage}
              variant="contained"
              color="primary"
              startIcon={zoomed ? <ZoomOutIcon /> : <ZoomInIcon />}
              aria-label={zoomed ? t('COMMON.ZOOM_OUT') : t('COMMON.ZOOM_IN')}
            >
              {zoomed ? t('COMMON.ZOOM_OUT') : t('COMMON.ZOOM_IN')}
            </Button>
          </DialogActions>
        </>
      )}

      {fileType === FileTypes.PDF && (
        <>
          <DialogContent
            style={{
              height: '80vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              padding: 0
            }}
            aria-label={t('COMMON.DOCUMENT_VIEW')}
          >
            {error ? (
              <Typography variant="h6" align="center" color="error" sx={{ p: 2 }}>
                {error}
              </Typography>
            ) : (
              <>
                <div
                  ref={pdfScrollRef}
                  style={{
                    width: '100%',
                    flex: 1,
                    padding: '24px',
                    overflowY: 'auto'
                  }}
                >
                  <Document
                    file={documentUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={() => setError(t('COMMON.PDF_LOAD_ERROR'))}
                    loading={
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          minHeight: '60vh',
                          width: '100%'
                        }}
                      >
                        <CircularProgress color="secondary" />
                      </Box>
                    }
                  >
                    {numPages ? (
                      <div
                        style={{
                          position: 'relative',
                          width: '100%',
                          height: `${pageVirtualizer.getTotalSize()}px`
                        }}
                      >
                        {pageVirtualizer.getVirtualItems().map(virtualItem => (
                          <div
                            key={virtualItem.key}
                            data-index={virtualItem.index}
                            ref={pageVirtualizer.measureElement}
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              transform: `translateY(${virtualItem.start}px)`,
                              paddingBottom: `${PDF_PAGE_GAP}px`,
                              display: 'flex',
                              justifyContent: 'center'
                            }}
                          >
                            <div
                              style={{
                                boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                                maxWidth: '100%'
                              }}
                            >
                              <Page
                                pageNumber={virtualItem.index + 1}
                                width={renderedPageWidth}
                                renderTextLayer={false}
                                renderAnnotationLayer={false}
                                loading={
                                  <CircularProgress color="secondary" size={24} />
                                }
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </Document>
                </div>
              </>
            )}
          </DialogContent>
          <DialogActions className={classes.dialogActions}>
            <Grid container justifyContent="center">
              <Button
                className={classes.buttons}
                onClick={() => {
                  getFileFromExternalLink(documentUrl, fileName);
                }}
                variant="contained"
                color="primary"
                startIcon={<FileDownloadIcon />}
                aria-label={t('COMMON.DOWNLOAD')}
              >
                {t('COMMON.DOWNLOAD')}
              </Button>
            </Grid>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

DocumentViewerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  documentUrl: PropTypes.string.isRequired,
  fileName: PropTypes.string
};

export default DocumentViewerDialog;
